import {BrowserRouter, Location, Navigate, NavigateFunction, useLocation, useNavigate} from "react-router-dom";
import React, {FC} from "react";
import {JustChildren} from "../types";
import {withAuthenticationRequired} from "@auth0/auth0-react";
import {KErrorPage, resetKTableDataParams, Translate} from "@kopjra/uikit";
import {createRoutes} from "./commons.ts";
import {AuthScope} from "../types/userstate.ts";
import {Feature, hasFeature} from "./features.ts";
import {useGetUserInfo} from "../api/user.ts";
import {BigWaiter} from "../components/BigWaiter.tsx";

export let navigate: NavigateFunction;
export let currentLocation: Location;

export interface ProtectedRouteProps {
    Component: React.ComponentType;
    allowedScopes?: AuthScope[];
}

function validScopes(userScopes?: AuthScope[], allowedScopes?: AuthScope[]): boolean {
    if (!allowedScopes) {
        return true;
    } else {
        return !!(userScopes && userScopes.some((u) => allowedScopes.indexOf(u) !== -1));
    }
}

export const ProtectedRoute: FC<ProtectedRouteProps> = ({ Component, allowedScopes }): React.ReactNode => {
    const {data: userInfo, isLoading} = useGetUserInfo();
    const WithAuthentication = withAuthenticationRequired(Component);

    function checkScopes() {
        if (validScopes(userInfo?.scopes || [], allowedScopes)) {
            return <Component/>;
        } else {
            return <KErrorPage code={"403"} title={<Translate i18nKey={"error.page"}/>}
                               message={<Translate i18nKey={"error.forbidden"}/>}/>;
        }
    }

    if (isLoading) {
        return <BigWaiter/>;
    }

    if (!userInfo) {
        return hasFeature(Feature.AUTH0) ? <WithAuthentication/> : <Navigate to={Routes.LOGIN}/>;
    } else {
        return checkScopes();
    }
}

export const NavigationExtractor: FC<JustChildren> = ({ children }: JustChildren) => {
    navigate = useNavigate();
    currentLocation = useLocation();
    return <>{children}</>;
}

export const HookedBrowserRouter: FC<JustChildren> = ({ children }: JustChildren) => (
    <BrowserRouter>
        <NavigationExtractor>{children}</NavigationExtractor>
    </BrowserRouter>
);

/**
 * This hook can be used in any component inside the HookedBrowserRouter
 * to get the query parameters' Map
 */
export function useQuery(): URLSearchParams {
    return new URLSearchParams(useLocation().search);
}

export function hasSessionId(location: Location): boolean {
    return !!location.pathname.match(/\/sessions\/.+/);
}

export const Routes = createRoutes({
    ALL: "*",
    HOME: "/",
    USER_ACCOUNT: "/user/account",
    LOGIN: "/login",
    SESSIONS: "/sessions",
    A_SESSION: (id?: string) => id ? `/sessions/${id}` : "/sessions/:sessionId",
    NEW_SESSION: "/new_session",
    NAVIGATE: (id?: string) => id ? `/navigate/${id}` : "/navigate/:sessionId",
    ADMIN_USERS: "/admin/users",
    ADMIN: "/admin",
    MY_SESSIONS: "/my_sessions",
    ADMIN_SESSIONS: (id?: string) => id ? `/admin/sessions?userId=${id}` : "/admin/sessions",
}, {
    extAdmin_sessions: () => {resetKTableDataParams("admin/sessions/list")},
    extSessions: () => {
        if (currentLocation?.pathname.indexOf("/navigate") !== -1) {
            window.location.replace("/sessions");
        }
    }
});



